import {
	Box,
	Button,
	Grid,
	IconButton,
	Chip,
	Stack,
	CircularProgress,
	Alert,
	Typography,
	Paper,
	Divider,
} from '@mui/material';
import axios from 'axios';

import { ReactNode, useState, useMemo, useCallback } from 'react';
import { SelectInput, SelectArrayInput, SimpleList, List, BooleanInput, FormDataConsumer } from 'react-admin';
import { FaCheck, FaTrashAlt } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import constants from '../../constants';
import { DeleteSection } from './DeleteSection';
import { membersShoices } from '../../utils/constants';

const AddAllowedEmailsOrPhones = ({ onPress, title }: { onPress: (v: string) => Promise<void>; title?: ReactNode }) => {
	const [text, setText] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	return (
		<Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
					{title}
				</Typography>
				<Divider />
				<textarea
					value={text}
					onChange={(e) => setText(e.target.value)}
					rows={5}
					className="border rounded-lg w-full p-2 outline-none focus:border-blue-500"
					placeholder="הזן רשימת ערכים מופרדים בפסיק או שורה חדשה"
					dir="rtl"
				/>
				<Button
					variant="contained"
					disabled={!text || isSubmitting}
					onClick={async () => {
						setIsSubmitting(true);
						try {
							await onPress(text);
							setText('');
						} catch (e) {
							console.error(e);
						} finally {
							setIsSubmitting(false);
						}
					}}
					sx={{ alignSelf: 'flex-end' }}
				>
					{isSubmitting ? 'מוסיף...' : 'הוספה'}
				</Button>
			</Box>
		</Paper>
	);
};

interface AuthOptionsProps {
	record: any;
	showUpdateVerifierModal: (domain_id: string, verifierEmail: string) => void;
	showAddDomainModal: () => void;
	refetch: () => void;
}

const AuthOptions = ({ record, showUpdateVerifierModal, showAddDomainModal, refetch }: AuthOptionsProps) => {
	const { allowed_emails, allowed_phones } = record;

	const emailChoices = useMemo(
		() => allowed_emails?.map((email: string) => ({ id: email, name: email })) || [],
		[allowed_emails]
	);

	const phoneChoices = useMemo(
		() => allowed_phones?.map((phone: string) => ({ id: phone, name: phone })) || [],
		[allowed_phones]
	);

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} md={6}>
				<Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
								דומייני חברה
							</Typography>
							<Button variant="outlined" onClick={showAddDomainModal}>
								הוספת דומיין
							</Button>
						</Box>
						<Divider />
						<Stack spacing={1}>
							{record?.domains?.map(
								({
									domain,
									verified,
									verifierEmail,
									_id,
								}: {
									_id: string;
									domain: string;
									verifierEmail: string;
									verified: boolean;
								}) => (
									<Box
										key={_id}
										sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
									>
										<Typography>{domain}</Typography>
										<Typography variant="body2" color="textSecondary">
											{verifierEmail}
										</Typography>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Typography color={verified ? 'green' : 'red'}>
												{verified ? <FaCheck /> : <FaX />}
											</Typography>
											<IconButton
												size="small"
												onClick={async () => {
													await axios.delete(
														`${constants.API_URL}/company/${record._id}/domain`,
														{
															data: { domain: _id },
														}
													);
												}}
											>
												<FaTrashAlt />
											</IconButton>
											{!verifierEmail && (
												<Button onClick={() => showUpdateVerifierModal(_id, domain)}>
													עדכון מייל לאימות
												</Button>
											)}
										</Box>
									</Box>
								)
							)}
						</Stack>
					</Box>
				</Paper>
			</Grid>

			<Grid item xs={12} md={6}>
				<AddAllowedEmailsOrPhones
					title={
						<span>
							{`הוספת ${record.auth_type === 'phone' ? 'מספרי טלפון' : 'מיילים'} מאושרים`}
							<span className="text-xs">{` (${
								(record.auth_type === 'phone'
									? record.allowed_phones?.length
									: record.allowed_emails?.length) ?? 0
							} קיימים)`}</span>
						</span>
					}
					onPress={async (values) => {
						const endpoint = record.auth_type === 'phone' ? 'add_allowed_phones' : 'add_allowed_emails';
						const res = await axios.post(`${constants.API_URL}/company/${record._id}/${endpoint}`, {
							values,
						});

						alert(
							`נוספו ${res.data.addedCount}${
								res.data.errors?.length ? `\n\nנכשלו:\n${res.data.errors.join('\n')}` : ''
							}`
						);
						refetch();
					}}
				/>
			</Grid>

			<Grid item xs={12}>
				<DeleteSection
					type={record.auth_type === 'phone' ? 'phone' : 'email'}
					choices={record.auth_type === 'phone' ? phoneChoices : emailChoices}
					companyId={record._id}
					onSuccess={refetch}
				/>
			</Grid>

			<Grid item xs={12}>
				<Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
							הגדרות הרשמה
						</Typography>
						<Divider />
						<SelectInput
							label="אמצעי הזדהות"
							fullWidth
							source="auth_type"
							choices={membersShoices}
							helperText="יש לשמור לפני עדכון פרטים נוספים"
						/>
						<FormDataConsumer>
							{({ formData }) =>
								(formData?.auth_type ?? 'email') === 'email' ? (
									<BooleanInput
										source="allow_phone_login"
										label="אפשר גם הזדהות בטלפון (מרשימת הטלפונים המאושרים)"
										helperText="מאפשר לאנשים ספציפיים ב-allowed_phones להתחבר בטלפון (OTP), בנוסף לזיהוי לפי דומיין המייל"
										defaultValue={false}
									/>
								) : null
							}
						</FormDataConsumer>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
};

export default AuthOptions;
